import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  GetRoleDTO,
  RoleDTO,
  RoleSearchReqDTO,
  RoleSearchResDTO
} from 'src/models/role';
import { userManagementAPIRoutes } from '..';

// Define a service using a base URL and expected endpoints
/**
 * roleServices is a collection of API endpoints for role management.
 */
export const roleServices = createApi({
  reducerPath: 'roleServices',
  tagTypes: ['roleTag'],
  baseQuery: fetchBaseQuery({
    baseUrl: userManagementAPIRoutes.baseUrl,
    prepareHeaders: (headers) => {
      // If we have a token set in local storage, let's assume that we should be passing it.
      const token = localStorage.getItem('jwtToken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  endpoints: (builder) => ({
    /**
     * createRole is an endpoint for creating a new role.
     */
    createRole: builder.mutation<RoleDTO, RoleDTO>({
      query: (body) => ({
        url: `${userManagementAPIRoutes.roles.endpoint}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['roleTag']
    }),

    /**
     * Get Role  is an endpoint for getting all the roles detail.
     */
    getRolesByMerchantId: builder.query<GetRoleDTO[], string>({
      query: (merchantId) => ({
        url: userManagementAPIRoutes.roles.endpoint,
        method: 'GET'
      })
    }),
    /**
     * findRoleById is an endpoint for finding a role by their ID.
     */
    findRoleById: builder.mutation<RoleDTO, number>({
      query: (id) => ({
        url: `${userManagementAPIRoutes.roles.endpoint}/${id}`,
        method: 'GET'
      })
    }),
    /**
     * updateRole is an endpoint for updating a role.
     */
    updateRole: builder.mutation<
      RoleDTO,
      {
        body: RoleDTO;
        id: number;
      }
    >({
      query: ({ body, id }) => ({
        url: `${userManagementAPIRoutes.roles.endpoint}/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: ['roleTag']
    }),
    /**
     * deleteRole is an endpoint for deleting a role.
     */
    deleteRole: builder.mutation<any, { id: number; deleteType: string }>({
      query: (body) => ({
        url: `${userManagementAPIRoutes.roles.endpoint}/${body?.id}/${body?.deleteType}`,
        method: 'DELETE',
        body
      }),
      invalidatesTags: ['roleTag']
    }),
    /**
     * getAllRoleBySearchCriteria is an endpoint for getting all roles.
     */
    getAllRoleBySearchCriteria: builder.query<
      RoleSearchResDTO,
      RoleSearchReqDTO
    >({
      query: (body) => ({
        url: `${userManagementAPIRoutes.roles.endpoint}/${userManagementAPIRoutes.roles.getAllRoleBySearchCriteria}`,
        method: 'POST',
        body
      }),
      providesTags: ['roleTag']
    })
  })
});

export const {
  useCreateRoleMutation,
  useFindRoleByIdMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
  useGetAllRoleBySearchCriteriaQuery,
  useGetRolesByMerchantIdQuery
} = roleServices;
